import { useWorkOrders } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import React, { useEffect, useState } from 'react';
import AppPageHeader from '../AppPageHeader';
import {
  ListWorkOrdersQuery,
  ListWorkOrdersResult,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { useDebounce } from '@/hooks';
import InfiniteTable from '../InfiniteTable';
import { getTableScrollX } from '@/utils/table';
import { ColumnsType } from 'antd/es/table/interface';
import { WorkorderColumn } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.types';
import { useIntl } from 'react-intl';
import moment from 'moment';
import Authorities from '@/types/Authority';
import EditIcon from '../EditIcon';
import { getUserFromLocalStorage } from '@/utils/user';
import { Button, Modal, ModalProps, Tooltip } from 'antd';
import StatusTag from '../StatusTag';
import {
  WorkOrderComplementStatus,
  workorderComplementColor,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import formatterPrice from '@/utils/number/formatterPrice';
import { useHistory } from 'react-router';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import WorkOrderListModalExtraFilters from './WorkOrderExtraFiltersInDashboard';
import { useWorkorderConfigurationProfile } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import Group from '../Group';
import ColorSwatch from '../ColorSwatch';
interface Props extends ModalProps {
  open: boolean;
  onCancel: VoidFunction;
  queryFilter?: ListWorkOrdersQuery;
  workOrderIsExists?: boolean;
  queryEnabled?: boolean;
}

const WorkOrdersListModal: React.FC<Props> = ({
  queryFilter,
  open,
  onCancel,
  workOrderIsExists,
  queryEnabled = true,
  ...props
}) => {
  const intl = useIntl();
  const history = useHistory();
  const [queryParams, setQueryParams] = useState<ListWorkOrdersQuery>({
    ...queryFilter,
    order: 1,
  });

  useEffect(() => {
    if (queryFilter) {
      setQueryParams((prev) => ({ ...prev, ...queryFilter }));
    }
  }, [open]);

  const { employeeId } = getUserFromLocalStorage();

  const queryDebounce = useDebounce({ ...queryParams, ...queryFilter }, 500);
  const { data, isFetching, hasNextPage, fetchNextPage, dataUpdatedAt } =
    useWorkOrders(queryDebounce, open && queryEnabled);

  const workOrders = flatInfiniteData(data);

  const profileConfiguration = useWorkorderConfigurationProfile();
  const profileConfigurationData = profileConfiguration.data?.data;

  const renderDate: ColumnsType<ListWorkOrdersResult> =
    queryFilter?.entryInitialDate && queryFilter.entryFinalDate
      ? [
          {
            key: WorkorderColumn.EntryDate,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.EntryDate}`,
            }),
            dataIndex: 'entryDate',
            render: (value) =>
              value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
            width: 90,
          },
        ]
      : [
          {
            key: WorkorderColumn.AuthorizationDate,
            title: intl.formatMessage({
              id: `settings.workorderConfiguration.${WorkorderColumn.AuthorizationDate}`,
            }),
            dataIndex: 'authorizationDate',
            render: (value) =>
              value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
            width: 90,
          },
        ];

  const renderColumnActions: ColumnsType<ListWorkOrdersResult> =
    workOrderIsExists
      ? [
          {
            key: 'actions',
            title: intl.formatMessage({
              id: 'commons.list.header.action',
            }),
            render: (_, record) => (
              <Button
                onClick={() =>
                  history.replace(
                    `/app/movements/workOrders/${record.workOrderId}/complements/${record.complementId}/generalInfo`,
                  )
                }
              >
                Selecionar
              </Button>
            ),
            ellipsis: true,
            width: 150,
            fixed: 'right',
            align: 'right',
          },
        ]
      : [
          {
            key: 'actions',
            title: intl.formatMessage({
              id: 'commons.list.header.action',
            }),
            render: (_, record) => (
              <EditIcon
                disabled={
                  !hasPermissions([
                    Authorities.MOVIMENTATION_WORKORDER_VIEW,
                    Authorities.MOVIMENTATION_WORKORDER_EDIT,
                  ]) ||
                  (record.estimator.id !== Number(employeeId) &&
                    !hasPermissions([
                      Authorities.MOVIMENTATION_WORKORDER_EDIT_OF_OTHER_BUDGETIST,
                    ]))
                }
                iconProps={{
                  onClick: () =>
                    window.open(
                      `/app/movements/workOrders/${record.workOrderId}/complements/${record.complementId}/generalInfo`,
                      '_blank',
                    ),
                }}
              />
            ),
            ellipsis: true,
            width: 50,
            fixed: 'right',
            align: 'right',
          },
        ];

  const columns: ColumnsType<ListWorkOrdersResult> = [
    {
      key: WorkorderColumn.Tag,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Tag}`,
      }),
      ellipsis: true,
      width: 70,
      render: (_, row) =>
        !!row.tag && (
          <Group position="center">
            <Tooltip
              title={row.tag.name.toUpperCase()}
              color={row.tag.color}
              placement="right"
            >
              <div>
                <ColorSwatch backgroundColor={row.tag.color} />
              </div>
            </Tooltip>
          </Group>
        ),
    },
    {
      key: WorkorderColumn.Number,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Number}`,
      }),
      dataIndex: 'complementIdentifier',
      ellipsis: true,
      width: 100,
    },
    {
      key: WorkorderColumn.Company,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Company}`,
      }),
      dataIndex: ['company', 'name'],
      ellipsis: true,
      width: 120,
    },
    {
      key: WorkorderColumn.Estimator,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Estimator}`,
      }),
      dataIndex: ['estimator', 'name'],
      ellipsis: true,
      width: 120,
    },
    {
      key: WorkorderColumn.Customer,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Customer}`,
      }),
      dataIndex: ['customer', 'name'],
      ellipsis: true,
      width: 120,
    },
    {
      key: WorkorderColumn.Insurer,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Insurer}`,
      }),
      dataIndex: ['insurer', 'name'],
      ellipsis: true,
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      key: WorkorderColumn.Plate,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Plate}`,
      }),
      dataIndex: 'licensePlate',
      ellipsis: true,
      width: 75,
      render: (value: string) => value || '-',
    },
    {
      key: WorkorderColumn.Vehicle,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Vehicle}`,
      }),
      dataIndex: 'vehicle',
      ellipsis: true,
      width: 120,
      render: (value: string) => value || '-',
    },
    ...renderDate,
    {
      key: WorkorderColumn.Status,
      title: intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Status}`,
      }),
      dataIndex: 'status',
      render: (value: WorkOrderComplementStatus) => (
        <StatusTag
          color={workorderComplementColor[value]}
          label={intl.formatMessage({ id: `workOrders.${value}` })}
        />
      ),
      ellipsis: true,
      width: 90,
    },
    {
      key: WorkorderColumn.Cost,
      title: `${intl.formatMessage({
        id: `settings.workorderConfiguration.${WorkorderColumn.Cost}`,
      })}($)`,
      dataIndex: 'price',
      render: (value: number) =>
        typeof value === 'number' ? formatterPrice(value) : '-',
      width: 100,
    },
    ...renderColumnActions,
  ];

  const handleCancel = () => {
    setQueryParams({ order: 1 });

    onCancel();
  };

  const activeColumns = columns.filter((item) =>
    profileConfigurationData?.some(
      (profile) =>
        (profile.active && item.key === profile.column) ||
        item.key === 'actions',
    ),
  );

  return (
    <Modal
      {...props}
      title="Listagem de orçamentos"
      width={1400}
      open={open}
      onCancel={handleCancel}
      footer={false}
    >
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
        headerActions={({ isShowingFilters }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() => setQueryParams({ ...queryFilter, order: 1 })}
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <WorkOrderListModalExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <InfiniteTable<ListWorkOrdersResult>
        loading={isFetching || profileConfiguration.isFetching}
        rowKey={(row) => row.workOrderId}
        dataSource={workOrders}
        columns={activeColumns}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        scroll={{ x: getTableScrollX(activeColumns) }}
        size="small"
        key={dataUpdatedAt}
      />
    </Modal>
  );
};

export default WorkOrdersListModal;
